<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { computed, onMounted, ref, watch } from "vue";

import BaseImage from "./BaseImage.vue";
import { useApi } from "../composables/useApi";
import { usePickupDraft } from "../composables/usePickupDraft";
import type { ProductDto } from "../types";

const api = useApi();
const pickupDraft = usePickupDraft();

const productCache = ref<Map<string, ProductDto>>(new Map());
const isExpanded = ref(false);

const lineItems = computed(() => pickupDraft.draft.lineItems);

const totalQuantity = computed(() => lineItems.value.reduce((sum, item) => sum + item.quantity, 0));

const enrichedLineItems = computed(() =>
  lineItems.value.map((item) => {
    const product = productCache.value.get(item.productSlug);
    return {
      product,
      quantity: item.quantity,
      slug: item.productSlug,
    };
  }),
);

const totalCents = computed(() => enrichedLineItems.value.reduce((sum, item) => sum + (item.product?.priceCents ?? 0) * item.quantity, 0));

const totalFormatted = computed(() =>
  new Intl.NumberFormat("de-DE", {
    currency: "EUR",
    style: "currency",
  }).format(totalCents.value / 100),
);

async function _ensureProductLoaded(slug: string) {
  if (productCache.value.has(slug)) {
    return;
  }
  try {
    const response = await api.get<{ product: ProductDto }>(`/products/${slug}`);
    productCache.value.set(slug, response.product);
  } catch {
    /* ignore — bar still renders without metadata */
  }
}

watch(
  lineItems,
  async (items) => {
    for (const item of items) {
      void _ensureProductLoaded(item.productSlug);
    }
    if (items.length === 0) {
      isExpanded.value = false;
    }
  },
  {
    deep: true,
    immediate: true,
  },
);

onMounted(() => {
  for (const item of lineItems.value) {
    void _ensureProductLoaded(item.productSlug);
  }
});

function increment(slug: string) {
  const existing = lineItems.value.find((item) => item.productSlug === slug);
  pickupDraft.addLineItem(slug, (existing?.quantity ?? 0) + 1);
}

function decrement(slug: string) {
  const existing = lineItems.value.find((item) => item.productSlug === slug);
  if (!existing) {
    return;
  }
  if (existing.quantity <= 1) {
    pickupDraft.removeLineItem(slug);
    return;
  }
  pickupDraft.addLineItem(slug, existing.quantity - 1);
}

function remove(slug: string) {
  pickupDraft.removeLineItem(slug);
}

function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
}
</script>

<template>
  <Teleport to="body">
    <Transition enter-active-class="transition-opacity duration-150" enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-100" leave-to-class="opacity-0">
      <div v-if="isExpanded" class="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" @click="isExpanded = false" />
    </Transition>

    <Transition enter-active-class="transition-transform duration-200" enter-from-class="translate-y-full"
      leave-active-class="transition-transform duration-150" leave-to-class="translate-y-full">
      <div v-if="lineItems.length > 0" class="fixed inset-x-0 bottom-0 z-50">
        <Transition enter-active-class="transition-transform duration-200" enter-from-class="translate-y-full"
          leave-active-class="transition-transform duration-150" leave-to-class="translate-y-full">
          <div v-if="isExpanded"
            class="border-t border-app-border bg-app-bg px-6 pb-5 pt-4 shadow-app-large">
            <div class="mx-auto w-full max-w-6xl">
              <div class="mb-3 flex items-center justify-between">
                <h2 class="text-base font-semibold text-app-text">Warenkorb</h2>
                <button type="button" class="rounded-full p-1 text-app-text-muted transition hover:bg-app-surface hover:text-app-text"
                  aria-label="Schließen" @click="isExpanded = false">
                  <Icon icon="heroicons:x-mark" class="h-5 w-5" />
                </button>
              </div>
              <ul class="max-h-[50vh] space-y-2 overflow-y-auto">
                <li v-for="item in enrichedLineItems" :key="item.slug"
                  class="flex items-center gap-3 rounded-xl bg-app-surface p-3">
                  <div class="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-app-surface-raised">
                    <BaseImage v-if="item.product?.thumbnailUrl" :alternative-text="item.product?.name ?? ''"
                      :source="item.product.thumbnailUrl" class="h-full w-full object-cover" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-medium text-app-text">
                      {{ item.product?.name ?? item.slug }}
                    </p>
                    <p v-if="item.product" class="text-xs text-app-text-muted">
                      {{ new Intl.NumberFormat("de-DE", {
                        currency: "EUR",
                        style: "currency",
                      }).format(item.product.priceCents / 100) }} × {{ item.quantity }}
                    </p>
                  </div>
                  <div class="flex items-center gap-1">
                    <button type="button"
                      class="flex h-8 w-8 items-center justify-center rounded-full bg-app-surface-raised text-app-text transition hover:bg-app-border-strong"
                      :aria-label="`${item.product?.name ?? item.slug} verringern`" @click="decrement(item.slug)">
                      <Icon icon="heroicons:minus" class="h-4 w-4" />
                    </button>
                    <span class="w-6 text-center text-sm font-semibold text-app-text">{{ item.quantity }}</span>
                    <button type="button"
                      class="flex h-8 w-8 items-center justify-center rounded-full bg-app-surface-raised text-app-text transition hover:bg-app-border-strong"
                      :aria-label="`${item.product?.name ?? item.slug} erhöhen`" @click="increment(item.slug)">
                      <Icon icon="heroicons:plus" class="h-4 w-4" />
                    </button>
                    <button type="button"
                      class="ml-1 flex h-8 w-8 items-center justify-center rounded-full text-app-text-subtle transition hover:bg-app-surface-raised hover:text-app-danger"
                      :aria-label="`${item.product?.name ?? item.slug} entfernen`" @click="remove(item.slug)">
                      <Icon icon="heroicons:trash" class="h-4 w-4" />
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </Transition>

        <div class="relative z-10 h-16 bg-app-shell text-app-text shadow-app-large">
          <div class="mx-auto flex h-full w-full max-w-6xl items-center justify-between gap-3 px-6">
            <button type="button" class="flex items-center gap-3" @click="toggleExpanded">
              <span
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-app-surface-raised text-sm font-semibold text-app-text ring-1 ring-app-border">
                {{ totalQuantity }}
              </span>
              <span class="text-left">
                <span class="block text-sm font-semibold">Warenkorb</span>
                <span class="block text-xs text-app-text-muted">{{ totalFormatted }}</span>
              </span>
            </button>
            <RouterLink to="/produkte/warenkorb"
              class="inline-flex items-center gap-2 rounded-full bg-app-accent px-4 py-2 text-sm font-semibold text-app-accent-text transition hover:bg-app-accent-strong">
              Abholzeit wählen
              <Icon icon="heroicons:arrow-right" class="h-4 w-4" />
            </RouterLink>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
